import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  SEC_FILTER_DEFAULTS,
  filedDatesToIso,
  getSecFilingFilterCatalogs,
  getSecFilingFilterSelections,
  hasSecFilingFiltersApplied,
  parseIsoDateLocal,
} from '../utils/secFilingFilters';

export default function useSecFilingFilters({
  syncSearchParams = false,
  searchParams,
  setSearchParams,
  filterOptions = null,
} = {}) {
  const { formTypes, xbrlTagCatalog, companyCatalog } = useMemo(
    () => getSecFilingFilterCatalogs({
      formTypes: filterOptions?.formTypes || [],
      xbrlTags: filterOptions?.xbrlTags || [],
      companies: filterOptions?.companies || [],
    }),
    [filterOptions],
  );

  const [filterCompany, setFilterCompany] = useState(SEC_FILTER_DEFAULTS.company);
  const [filterFormType, setFilterFormType] = useState(SEC_FILTER_DEFAULTS.type);
  const [filterXbrlTag, setFilterXbrlTag] = useState(SEC_FILTER_DEFAULTS.xbrlTag);
  const [filedStart, setFiledStart] = useState(null);
  const [filedEnd, setFiledEnd] = useState(null);
  const [tempFiledRange, setTempFiledRange] = useState([null, null]);
  const [showTableFilters, setShowTableFilters] = useState(false);

  useEffect(() => {
    if (!syncSearchParams || !searchParams) return;
    const company = searchParams.get('company');
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    setFilterCompany(company || SEC_FILTER_DEFAULTS.company);
    setFiledStart(parseIsoDateLocal(from));
    setFiledEnd(parseIsoDateLocal(to));
  }, [syncSearchParams, searchParams]);

  const { filedFromIso, filedToIso } = filedDatesToIso(filedStart, filedEnd);

  const {
    selectedCompanies,
    selectedFormTypes,
    selectedXbrlTags,
  } = useMemo(
    () => getSecFilingFilterSelections({
      filterCompany,
      filterFormType,
      filterXbrlTag,
      formTypes,
      xbrlTagCatalog,
      companyCatalog,
    }),
    [
      filterCompany,
      filterFormType,
      filterXbrlTag,
      formTypes,
      xbrlTagCatalog,
      companyCatalog,
    ],
  );

  const hasFiltersApplied = hasSecFilingFiltersApplied({
    selectedCompanies,
    selectedFormTypes,
    selectedXbrlTags,
    filedStart,
    filedEnd,
  });

  const handleClearFilters = useCallback(() => {
    setFilterCompany(SEC_FILTER_DEFAULTS.company);
    setFilterFormType(SEC_FILTER_DEFAULTS.type);
    setFilterXbrlTag(SEC_FILTER_DEFAULTS.xbrlTag);
    setFiledStart(null);
    setFiledEnd(null);
    setTempFiledRange([null, null]);
    if (syncSearchParams && setSearchParams) {
      setSearchParams({});
    }
  }, [syncSearchParams, setSearchParams]);

  useEffect(() => {
    if (!syncSearchParams || !searchParams || !setSearchParams) return;

    const fromParam = searchParams.get('from') || '';
    const toParam = searchParams.get('to') || '';
    const nextFrom = filedStart ? format(filedStart, 'yyyy-MM-dd') : '';
    const nextTo = filedEnd ? format(filedEnd, 'yyyy-MM-dd') : '';
    if (fromParam === nextFrom && toParam === nextTo) return;

    const next = new URLSearchParams(searchParams);
    if (filedStart) next.set('from', nextFrom);
    else next.delete('from');
    if (filedEnd) next.set('to', nextTo);
    else next.delete('to');
    setSearchParams(next);
  }, [filedStart, filedEnd, syncSearchParams, searchParams, setSearchParams]);

  return {
    filterCompany,
    setFilterCompany,
    filterFormType,
    setFilterFormType,
    filterXbrlTag,
    setFilterXbrlTag,
    filedStart,
    setFiledStart,
    filedEnd,
    setFiledEnd,
    tempFiledRange,
    setTempFiledRange,
    showTableFilters,
    setShowTableFilters,
    hasFiltersApplied,
    handleClearFilters,
    selectedCompanies,
    selectedFormTypes,
    selectedXbrlTags,
    filedFromIso,
    filedToIso,
    formTypes,
    xbrlTagCatalog,
    companyCatalog,
  };
}
